import React, { useState } from 'react';
import { getGradeLabel, getGradeRecommendation, getBirthYearOptions, getGradeNumber, MONTH_OPTIONS } from '../utils/gradeUtils';
import { trackEvent } from '../utils/analytics';

const AVATAR_OPTIONS = [
  '🐣','🐶','🐱','🐻','🐼','🦊','🐸','🐧',
  '🦁','🐯','🐨','🐮','🐷','🐙','🦄','🐬',
  '🦋','🌸','⭐','🌈','🚀','🎮','🎵','🍭',
];

/**
 * プロフィール作成・編集フォーム
 * isGuest: ゲストモードか
 * initialData: 編集時の初期値
 * onSave(profile): 保存完了コールバック
 * onCancel: キャンセル
 * isFirst: 初回作成（スキップボタンを表示）
 * onSkip: スキップコールバック
 */
export default function ProfileEdit({ isGuest, initialData, onSave, onCancel, isFirst, onSkip }) {
  const [name,       setName]       = useState(initialData?.name        ?? '');
  const [avatar,     setAvatar]     = useState(initialData?.avatar      ?? '🐣');
  const [birthYear,  setBirthYear]  = useState(initialData?.birth_year  ?? '');
  const [birthMonth, setBirthMonth] = useState(initialData?.birth_month ?? '');
  const [pin,        setPin]        = useState(initialData?.pin         ?? '');
  // 誰が設定しているか。機能は変えず、実態把握（GA4・DB）のためだけに聞く。
  // 学校モードではクラスの生徒はプロフィールを持たない想定だが、
  // 実際には生徒がログインして使っているケースもあり得るため確認する。
  const [setupRole,  setSetupRole]  = useState(initialData?.setup_role  ?? '');
  const [showPin,    setShowPin]    = useState(false); // PIN入力欄の表示
  const [saving,     setSaving]     = useState(false);

  const gradeLabel = (birthYear && birthMonth)
    ? getGradeLabel(Number(birthYear), Number(birthMonth))
    : '';
  const recommendation = (birthYear && birthMonth)
    ? getGradeRecommendation(Number(birthYear), Number(birthMonth))
    : null;

  const handlePinChange = (v) => {
    // 数字4桁のみ
    const digits = v.replace(/\D/g, '').slice(0, 4);
    setPin(digits);
  };

  const handleSave = async () => {
    setSaving(true);
    const trimmedName = name.trim() || (isGuest ? 'ゲスト' : 'こども');
    const yr = birthYear  ? Number(birthYear)  : null;
    const mo = birthMonth ? Number(birthMonth) : null;
    await onSave({
      name:        trimmedName,
      avatar,
      birth_year:  yr,
      birth_month: mo,
      pin:         pin || null,
      setup_role:  setupRole || null,
    });
    // GA4: プロフィール保存イベント
    trackEvent('ondoku_profile_saved', {
      has_name:   !!name.trim(),
      has_birth:  !!(yr && mo),
      has_pin:    !!pin,
      grade:      (yr && mo) ? (getGradeNumber(yr, mo) ?? 'unknown') : 'unknown',
      setup_role: setupRole || 'unanswered',
    });
    setSaving(false);
  };

  const hasPin = !!initialData?.pin;

  return (
    <div className="screen profile-edit-screen">

      {/* ヘッダー */}
      <div className="profile-edit-header">
        {onCancel && (
          <button className="auth-back-btn" onClick={onCancel}>← もどる</button>
        )}
        <h2 className="profile-edit-title">
          {initialData ? '✏️ プロフィールをへんしゅう' : '👋 プロフィールをつくろう'}
        </h2>
        {isFirst && (
          <p className="profile-edit-sub">あとで変えられるよ！名前はニックネームでもOK</p>
        )}
      </div>

      <div className="profile-edit-body">

        {/* アイコン選択 */}
        <div className="profile-edit-section">
          <div className="profile-edit-label">アイコン</div>
          <div className="avatar-grid">
            {AVATAR_OPTIONS.map((a) => (
              <button
                key={a}
                className={`avatar-btn ${avatar === a ? 'selected' : ''}`}
                onClick={() => setAvatar(a)}
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        {/* 名前 */}
        <div className="profile-edit-section">
          <div className="profile-edit-label">なまえ{isGuest ? '（任意）' : ''}</div>
          <input
            className="auth-input"
            type="text"
            placeholder={isGuest ? 'スキップもできるよ' : 'たろう　など'}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={12}
          />
        </div>

        {/* だれが設定しているか（機能には影響しない・実態把握用） */}
        <div className="profile-edit-section">
          <div className="profile-edit-label">
            せっていしているのは？（任意）
          </div>
          <div className="setup-role-row">
            {[
              { value: 'child',  label: 'こども',  icon: '🧒' },
              { value: 'parent', label: '保護者',  icon: '👪' },
              { value: 'teacher', label: '先生',   icon: '🧑‍🏫' },
              { value: 'other',  label: 'その他',  icon: '👀' },
            ].map((r) => (
              <button
                key={r.value}
                type="button"
                className={`setup-role-btn ${setupRole === r.value ? 'selected' : ''}`}
                // 選び直しやすいよう、同じものをもう一度押したら解除する
                onClick={() => setSetupRole(setupRole === r.value ? '' : r.value)}
              >
                <span className="setup-role-icon">{r.icon}</span>
                <span>{r.label}</span>
              </button>
            ))}
          </div>
          <div className="profile-edit-hint">
            使い方の参考にさせてください。選んでも表示や機能は変わりません
          </div>
        </div>

        {/* 生年月 */}
        <div className="profile-edit-section">
          <div className="profile-edit-label">
            {setupRole === 'child' || setupRole === ''
              ? 'うまれた年・月（任意）'
              : 'お子さんの生まれた年・月（任意）'}
          </div>
          <div className="birth-row">
            <select
              className="birth-select"
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
            >
              <option value="">年を選ぶ</option>
              {getBirthYearOptions().map((y) => (
                <option key={y} value={y}>{y}年</option>
              ))}
            </select>
            <select
              className="birth-select"
              value={birthMonth}
              onChange={(e) => setBirthMonth(e.target.value)}
            >
              <option value="">月を選ぶ</option>
              {MONTH_OPTIONS.map((m) => (
                <option key={m} value={m}>{m}月</option>
              ))}
            </select>
          </div>

          {/* 学年表示 */}
          {gradeLabel && (
            <div className="grade-badge-row">
              <span className="grade-badge">📚 {gradeLabel}</span>
              {recommendation && (
                <span className="grade-hint">→ {recommendation.hint}</span>
              )}
            </div>
          )}
        </div>

        {/* PIN設定（初回作成では非表示） */}
        {!isFirst && (
          <div className="profile-edit-section">
            <div className="profile-edit-label">
              🔒 ひみつナンバー（PIN）
              <span style={{ fontSize: 12, fontWeight: 400, color: '#888', marginLeft: 8 }}>
                {hasPin ? '設定済み' : '未設定・任意'}
              </span>
            </div>

            {!showPin ? (
              <button
                className="pin-set-btn"
                onClick={() => setShowPin(true)}
              >
                {hasPin ? '🔑 PINをかえる・けす' : '＋ PINをせってい'}
              </button>
            ) : (
              <div>
                <div className="auth-input-wrap">
                  <input
                    className="auth-input"
                    type="number"
                    inputMode="numeric"
                    placeholder="4けたの数字"
                    value={pin}
                    onChange={(e) => handlePinChange(e.target.value)}
                    maxLength={4}
                  />
                </div>
                <p style={{ fontSize: 12, color: '#888', margin: '6px 0 0' }}>
                  空のまま保存するとPINが削除されます
                </p>
              </div>
            )}
          </div>
        )}

      </div>

      {/* ボタン群 */}
      <div className="profile-edit-actions">
        <button
          className="auth-btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '…' : initialData ? '✅ こうしんする' : '✅ このプロフィールではじめる'}
        </button>

        {isFirst && onSkip && (
          <button className="auth-btn-switch" onClick={onSkip}>
            名前なしで　すぐためしてみる
          </button>
        )}
      </div>

    </div>
  );
}
